// Web-side completion-options for the human-completed action kinds (keyed by demodulized
// actionType). This is the ONLY action knowledge left on the FE, and it's temporary: action
// label / description / required / produces now come from the API (OLIO-2574), and the option
// buttons (label / tone) + their output mapping are slated to move to the definition next
// (Phase 2). Kinds with no options (the automated AI pair) aren't listed — the FE renders them
// purely from the API. When options land in the API, this file goes away entirely.
import Workflow from 'models/Workflow';

export interface ActionOption {
  label: string;
  tone?: 'primary' | 'danger';
  // Exactly the `outputs` body to PATCH when this option is chosen. The action's own output
  // only — compound consequences (e.g. a possible duplicate ending the referral) are derived
  // by the kind on the backend, not posted from here (OLIO-2574 Phase A).
  outputs: Record<string, string>;
}

export interface ActionKindMeta {
  // Shown above the options when the action needs a person.
  prompt?: string;
  // The manual completion choices; omitted kinds render status-only.
  options?: ActionOption[];
  // The declared value whose stored content identifies the chosen option, so a
  // completed card can show the recorded result.
  resultValueName?: string;
  // Friendly display text for resultValueName content when the kind has no options
  // to derive a label from (automated kinds). Falls back to the raw value.
  resultLabels?: Record<string, string>;
  // A link rendered with the completed result — e.g. the referral a disposition
  // created or merged into. The target id is read from the named value.
  resultLink?: { valueName: string; label: string; path: (id: string) => string };
  // Value names summarized on the completed card (e.g. what retrieval pulled).
  resultSummaryValues?: string[];
  // For fanned-out (per-building) kinds: short labels for the header rollup counts,
  // keyed by the recorded result value ("2 Accepted • 1 Denied • 1 Pending").
  countLabels?: Record<string, string>;
  // Instructional copy + inline actions when the step is flagged (a person must supply
  // something before it can run) — the Figma flagged card. Kinds without these fall
  // back to naming the missing values. `flaggedPromptSatisfied` replaces the copy when
  // the step's produced values already exist (manual entry): the flag isn't a failure
  // then, and we must not tell the user to do what they already did.
  flaggedPrompt?: string;
  flaggedPromptSatisfied?: string;
  // `hideWhenSatisfied` / `hideWhenUnsatisfied` gate a link on whether the step's outputs are
  // already accounted for. Both exist because the two states want different remedies: nothing
  // extracted wants the manual entry, while values-already-there wants the upload invitation its
  // copy makes. A link with neither flag shows in both.
  flaggedLinks?: {
    label: string;
    section: 'documents' | 'demographics';
    hideWhenSatisfied?: boolean;
    hideWhenUnsatisfied?: boolean;
  }[];
  // A per-building kind that must still read as ONE row, not a fan-out panel.
  //
  // The default for a group-stamped kind is a FanoutPanel — one row per building, which is right when
  // every building has its own answer to give (Clinical Review, Facility Decision). It is wrong when
  // the fan-out exists only so the work can be SCOPED: the EHR sync is per-building because a push
  // into a building's EHR is that building's work, but exactly one building is ever asked to make it,
  // and the rest park `skipped`. Rendering those would put a row — and pre-decision a Send button —
  // in front of every losing building. On a 90-building referral that is 90 rows.
  //
  // The representative is the instance for the SELECTED facility (see representativeFor), not the
  // first and not the first non-skipped: after a winner moves post-landing those diverge, and picking
  // wrong shows another building's card at the moment someone is moving a real patient.
  collapseFanout?: boolean;
  // The skipped (Not Applicable) row treatment (OLIO-2633, Option C): explanatory copy
  // and, when the step can still be fed later, the affordance that revives it.
  skippedCopy?: string;
  skippedAction?: { label: string; section: 'documents' | 'demographics' };
  // Render a row for this kind ONLY in the states where a person owes it something —
  // action_required, flagged, or errored — and nothing at all otherwise (OLIO-3158).
  //
  // That set is the API's Workflow::Evaluator::HUMAN_NEEDED_STATUSES, and matching it is a
  // correctness requirement rather than a tidy coincidence: the engine uses the same set to decide
  // whether the RUN bubbles to action_required. Hide a row in a state the engine counts and you
  // get a referral flagged for attention with nothing on screen saying why.
  //
  // Not "hide when skipped": a rule keyed on one resting status would show and hide the row as a
  // referral moved between the others, which is the flicker this avoids.
  //
  // Confirm Referral Source is applicable only when a source can't be matched, so it is parked
  // almost always, and it deliberately never completes — answering it parks it again rather than
  // finishing it. Those are the only two states it holds, so "only while asking" is a stable
  // rule and not a moving one.
  //
  // Opt-in per kind, not a general noise switch. The parked document steps SHOULD explain
  // themselves: someone expected them to run, and "Documents weren't provided" is the answer.
  // Use this only where being parked means the step succeeded at having nothing to do.
  //
  // Only safe for a kind whose quiet states are TERMINAL (`skipped` / `canceled`). Hiding the row
  // keeps it out of the card's badge and the rail's dot, but `categorySegments` and
  // `progressSummary` (`referralProgress.ts`) still read the raw list and feed the referral list's
  // segments, its "X of N", and the overall pill. A kind that rests at `pending` or `running` while
  // quiet would therefore read Complete on the card and Pending on the list. Confirm Referral
  // Source rests at `skipped`, `action_required` or `completed` — OLIO-3302 added the last one, and
  // `hiddenWhileQuiet` exempts it precisely so a row someone just answered is not hidden from the
  // card while the list counts it. The next kind to take this flag has to be checked against those
  // two functions first (Seth, PR review).
  renderOnlyWhenHumanNeeded?: boolean;
  // Which states this kind renders a body in, beyond what the fields above already imply
  // (`options` for action_required, `resultValueName`/`resultSummaryValues` for completed).
  // These three used to be bare `action.kind === '...'` comparisons inside CategoryCard: a
  // typo there silently killed the branch, and a kind renamed on the API side broke it with
  // no compile error. Declared here so per-kind knowledge lives beside the rest of it.
  //
  // `bodyWhenActionRequired` is for kinds whose action_required state renders a bespoke form
  // or panel rather than generic option buttons.
  bodyWhenActionRequired?: boolean;
  // For a kind that is ACTIONABLE WHILE PENDING — its body owns the pending state, so the generic
  // "waiting on…" fallback must not also render.
  //
  // Some kinds suppress activation on purpose (their `run` is a no-op), because an ungated manual
  // action would otherwise read action_required from birth and poison every attention signal for the
  // run's whole life. For those, `pending` IS the ready-to-act state and "waiting on…" over the
  // button is simply wrong. AcceptReferral has the same property but predates this flag — it renders
  // through its own branch in ActionBody's ternary, which already excludes the fallback.
  bodyWhenPending?: boolean;
  // For kinds that complete autonomously and still own a completed surface without exposing
  // a recorded result value to render.
  detailWhenCompleted?: boolean;
  // For kinds whose failure state is itself actionable (portal retrieval falls back to the
  // manual form as recovery).
  detailWhenErrored?: boolean;
  // For a kind whose IN-FLIGHT state has something to say. Most don't — a running action's header
  // already reads "Running", and a body would just repeat it. A kind that reaches out to an external
  // system does: "Sending to MatrixCare…" tells the coordinator what is happening to a real patient
  // record, and in a multi-action category the row would otherwise render blank.
  detailWhenRunning?: boolean;
  // For kinds a headless run drives server-side (OLIO-2888): a RUNNING action owns body
  // copy ("running automatically…") when the workflow's mode is headless. Attended
  // running stays a status-only row — the gate also checks workflow.retrievalMode.
  detailWhenRunningHeadless?: boolean;
}

export const ACTION_KINDS: Record<string, ActionKindMeta> = {
  ProcessAttachments: {
    // Completes with a packet summary rather than a recorded result value.
    detailWhenCompleted: true,
    flaggedPrompt: 'Upload referral documentation for Olio to extract patient demographics and clinical details.',
    flaggedLinks: [{ label: 'Add documents', section: 'documents' }],
    skippedCopy: 'Documents weren’t provided — demographics were entered manually.',
    skippedAction: { label: 'Add Documents', section: 'documents' },
  },
  ExtractAttributes: {
    // Completes with the extracted-demographics readout rather than a recorded result value.
    detailWhenCompleted: true,
    flaggedPrompt: 'Demographic information could not be extracted from documentation.',
    flaggedPromptSatisfied:
      'Demographics were added manually. Upload the referral documentation anytime and Olio will process it.',
    // Nothing extracted → offer manual entry ONLY. The document step sits directly above this row
    // already offering its own Try again / Add or remove documents, so a second upload link here is
    // noise. The satisfied case is the reverse: its copy invites the upload, so that link is all it
    // shows.
    flaggedLinks: [
      { label: 'Add documents', section: 'documents', hideWhenUnsatisfied: true },
      { label: 'Manually add demographics', section: 'demographics', hideWhenSatisfied: true },
    ],
    skippedCopy: 'Nothing to extract — demographics were entered manually.',
  },
  ResolveAttrValues: {
    // Invisible unless it needs a name from a person — see renderOnlyWhenHumanNeeded.
    renderOnlyWhenHumanNeeded: true,
    bodyWhenActionRequired: true,
    // It completes now (OLIO-3302), and answering is the only way it gets there, so the completed
    // row owns a surface: what the referral reports under. Without this, a category where this is
    // the only visible row renders the status-only fallback and that surface never mounts.
    detailWhenCompleted: true,
  },
  DuplicatePatientSearch: {
    // action_required renders the candidate-review body (DuplicatePatientReview,
    // OLIO-2729) instead of generic option buttons.
    bodyWhenActionRequired: true,
    resultValueName: 'duplicate_patient_result',
    resultLabels: {
      no_match: 'No duplicate referrals found',
      confirmed_match: 'Confirmed duplicate — this referral was canceled',
      // Legacy value from the pre-OLIO-2729 stub contract; kept so old completions render.
      possible_match: 'Possible duplicate found',
    },
  },
  // SexOffenderCheck is integration-backed (offenders.io via the Olio integration platform) and
  // adjudicated in a dedicated slideout — SexOffenderCheckReview — that owns its Dismiss/Match
  // mutations against a run-scoped `sex_offender_match_reviews` value. The entry declares the
  // rendering shape (completed card is a slideout trigger with a detail body) via the same
  // declarative fields DenialCheck uses; it deliberately omits `options`/`resultValueName` so no
  // generic option-button prompt renders on top of the slideout trigger.
  SexOffenderCheck: {
    detailWhenCompleted: true,
  },
  // DenialCheck runs and completes automatically; a match is surfaced (via the API's
  // effectiveStatus) as a flag on the completed card, and its own Review Flag slideout
  // (DenialCheckReview) is READ-ONLY — it lists the matched entries the check found; there is no
  // confirm/dismiss and no mutation (the determination is derived server-side).
  //
  // This entry used to be deliberately absent, with a note saying not to add one, because the kind
  // uses none of the generic meta: no option buttons, no CompletedResult. That still holds — and
  // the entry below adds no option or result fields. What it declares is only WHICH STATES render
  // a body, which previously lived as bare `action.kind === 'DenialCheck'` comparisons inside
  // CategoryCard. Absent-from-the-registry didn't remove the per-kind knowledge, it just moved it
  // somewhere a typo could silently disable it.
  DenialCheck: {
    detailWhenCompleted: true,
  },
  BenefitsEligibility: {
    prompt: 'Record the insurance verification result.',
    resultValueName: 'benefits_eligibility_result',
    options: [
      { label: 'Coverage active', outputs: { benefits_eligibility_result: 'active' } },
      { label: 'Coverage inactive', tone: 'danger', outputs: { benefits_eligibility_result: 'inactive' } },
    ],
  },
  // FacilityDecision's Accept/Deny interaction (buttons, note modal, bulk links) lives
  // entirely in FacilityDecisionPanel — only the rollup/result display metadata is here.
  FacilityDecision: {
    resultValueName: 'facility_determination',
    countLabels: { accepted: 'Accepted', denied: 'Denied' },
  },
  // AcceptReferral ("Referral Outcome") completes through its own always-available panel
  // (ReferralOutcomePanel) and renders its completed state as per-building verdict rows
  // (ReferralOutcomeResult) — only the result declaration lives here.
  AcceptReferral: {
    bodyWhenActionRequired: true,
    resultValueName: 'final_outcome',
  },

  // Portal work never renders on this step page — the Tasks surface owns it (the retrieval,
  // facility-mapping and discovery kinds moved there in the redesign). The disposition entry below
  // stays for the task slideout's labels/link; Sync Patient to EHR stays because it is not portal
  // work — it is the referral's own EHR push.

  // Sync Patient to EHR (OLIO-2846) — the push into the winning facility's MatrixCare or PCC.
  //
  // Deliberately NO `resultValueName`, even though the kind declares `sync_patient_to_ehr_result`.
  // With one, `hasActionDetail` passes and ActionBody renders the generic <CompletedResult>, whose
  // rawLabel hits a `typeof value === 'string'` guard — and this result is a structured object, so it
  // would render an empty <Result> shell above our own body. SyncPatientBody reads the result itself.
  //
  // No `options` either: this action is started by a person clicking Send, not by choosing a
  // completion outcome, and its states are driven by what the vendor said. `detailWhenErrored` keeps
  // the Retry affordance on screen; Retry is `rerun` then `dispatch`, because `errored` is not in the
  // API's DISPATCHABLE_FROM (design 010 — one attempt per instance).
  //
  // NO `bodyWhenPending`, deliberately — it was here while the kind's `run` was a no-op and `pending`
  // was the ready-to-send state. Under design 010 the kind declares the decision's outputs as required
  // inputs, so `pending` now means the referral is UNDECIDED and there is nothing to send;
  // `action_required` is the ready state. Leaving the flag on would render "Ready to send" on every
  // referral nobody has decided yet. Pending falls through to the definition's own description, which
  // is the honest quiet surface.
  SyncPatientToEhr: {
    // Per-building since OLIO-2846 review round two, but only ever one building acts — see
    // collapseFanout. Without this the card becomes a FanoutPanel with one row per targeted building.
    collapseFanout: true,
    bodyWhenActionRequired: true,
    detailWhenRunning: true,
    detailWhenCompleted: true,
    detailWhenErrored: true,
  },
  DisposePortalReferral: {
    resultValueName: 'portal_automation_outcome',
    resultLabels: {
      created: 'Created a new referral',
      merged: 'Merged into an existing referral',
    },
    resultLink: {
      valueName: 'disposed_workflow_id',
      label: 'View referral',
      path: (id: string) => `/inbound-referrals/${id}`,
    },
  },
};

export function actionKindMeta(kind: string): ActionKindMeta | undefined {
  return ACTION_KINDS[kind];
}

// A stopgap humanization of a declared (snake_case) value name (`patient_dob` ->
// `Patient dob`), used only when the API hasn't supplied a catalog label.
export function valueLabel(name: string): string {
  const spaced = name.replace(/_/g, ' ').trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

// The display label for a value, best source first (OLIO-3022):
//
//   1. the label on the run's own row for it (design P1-5);
//   2. the definition's value catalog — the same label, served for values the run doesn't hold;
//   3. the caller's own declared label (a field registry's), for a definition serving no catalog;
//   4. the humanized name.
//
// Rungs 2 and 3 exist because rung 1 degrades exactly where the label matters most: a MISSING
// value has no row, so a by-property surface asking for the one field nobody filled would have
// labeled it `Patient dob`.
export function labelForValue(workflow: Workflow, name: string, fallback?: string): string {
  return (
    workflow.valueFor(name)?.label ?? workflow.definition.catalogEntry(name)?.label ?? fallback ?? valueLabel(name)
  );
}
